import ExcelJS from "exceljs";
import { FormattedMessage, useIntl } from "react-intl";

import "./spreadSheetImport.scss";
import { ReactComponent as Template } from "assets/images/icons/TopNavIcons/TemplatesIcon.svg";
import { dangerousGoodsOptions, temperatureControlledOptions } from "../../PackingListDefaultOptions";

type ExcelSheet = ExcelJS.Worksheet;
type DataArrayItem = [string, string] | string[][];
type LabelArrayItem = {
  label: string;
  is_active: boolean;
  value: string;
};
type DropdownFormula = string[];
interface HandlingUnitOption {
  identifier: string;
  label: string;
  container: any;
}
interface DownloadTemplateProps {
  packingListConfig: {
    crating_required_enabled: boolean;
    dangerous_goods_enabled: boolean;
    temperature_controlled_enabled: boolean;
    hazardous_goods_enabled: boolean;
    nmfc_number_enabled: string;
    customs_value_setting: string;
    handling_unit_options: HandlingUnitOption[];
    nmfc_values: string[][];
  };
}

function createCellDropdowns(sheet: ExcelSheet, column: string | null, values: string[]) {
  if (!column) return;
  const startRow: number = 2;
  const endRow: number = 20;

  for (let row: number = startRow; row <= endRow; row += 1) {
    const cellAddress: string = `${column}${row}`;
    sheet.getCell(cellAddress).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: [values.join(",")],
    };
  }
}

// Columns are dynamic, this function takes a header as string and returns the letter of the column it belongs to.
function getColumnLetterByHeader(sheet: ExcelSheet, header: string): string | null {
  const headerRow = sheet.getRow(1);
  // @ts-ignore
  const columnIndex = headerRow.values.indexOf(header) - 1;
  if (columnIndex !== -1) {
    const columnLetter = String.fromCharCode("A".charCodeAt(0) + columnIndex);
    return columnLetter;
  }
  return null; // Header not found or row/values are null/undefined
}

function createNMFCDropdown(data: DataArrayItem[]): DropdownFormula {
  const concatenatedValues = data.map(([number]) => number).join(", ");
  return [`"${concatenatedValues}"`];
}

function createLabelDropdown(data: LabelArrayItem[]): DropdownFormula {
  const concatenatedLabels = data.map((item) => (item.label ? item.label : item.value)).join(", ");
  return [`"${concatenatedLabels}"`];
}

export function generatePackingListTemplate(packingListConfig: DownloadTemplateProps["packingListConfig"], intl: ReturnType<typeof useIntl>) {
  const {
    crating_required_enabled,
    dangerous_goods_enabled,
    temperature_controlled_enabled,
    hazardous_goods_enabled,
    nmfc_number_enabled,
    customs_value_setting,
    handling_unit_options,
  } = packingListConfig;

  const labels: string[] = handling_unit_options
    .filter((item) => Object.values(item)[0].identifier === "default_")
    .map((item) => Object.values(item)[0].label);
  const handlingUnitValues = [`"${labels.join(",")}"`];

  const dangerousGoodsValues = createLabelDropdown(dangerousGoodsOptions(intl));
  const temperateControlValues = createLabelDropdown(temperatureControlledOptions(intl));
  const NMFCValues = createNMFCDropdown(
    (packingListConfig?.nmfc_values || []).map((innerArray) => [innerArray[0], innerArray[1]]),
  );
  const containerValues = createLabelDropdown(
    packingListConfig.handling_unit_options.find((unit: any) => unit.container)?.container.handling_unit_options,
  );

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Packing List");

  const hardcodedHeaders = [
    "Handling Unit",
    "Container Type",
    "Length",
    "Width",
    "Height",
    "Number of Units",
    "Piece weight",
    "Stackable",
  ];

  const dynamicHeaders = [];
  if (nmfc_number_enabled !== "Off") dynamicHeaders.push("NMFC Class");
  if (hazardous_goods_enabled) dynamicHeaders.push("Hazardous");
  if (crating_required_enabled) dynamicHeaders.push("Crating required by");
  if (dangerous_goods_enabled) dynamicHeaders.push("Dangerous goods");
  if (temperature_controlled_enabled) dynamicHeaders.push("Temperature Controlled");

  const customsInfoHeaders = ["HS Code", "Value", "Currency", "Commodity information"];

  const headers = [...hardcodedHeaders, ...dynamicHeaders];
  if (customs_value_setting !== "Off") headers.push(...customsInfoHeaders);

  sheet.addRow(headers);

  const headerRow = sheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });

  if (customs_value_setting !== "Off") {
    customsInfoHeaders.forEach((header, index) => {
      const cell = sheet.getRow(1).getCell(hardcodedHeaders.length + dynamicHeaders.length + index + 1);
      const bgColor = index % 2 === 0 ? "8BCAFD" : "7FBCEE";
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
    });
  }

  const exampleRowValues = ["crate", "", "10", "20", "30", "4", "50", "X"];
  const exampleRowValues2 = ["container", "20Ft Standard", "", "", "", "4", "50", "X"];

  if (nmfc_number_enabled !== "Off") exampleRowValues.push("Any");
  if (hazardous_goods_enabled) exampleRowValues.push("X");
  if (crating_required_enabled) exampleRowValues.push("15-11-2025");
  if (dangerous_goods_enabled) exampleRowValues.push("Any");
  if (temperature_controlled_enabled) exampleRowValues.push("Any");

  sheet.addRow(exampleRowValues);
  sheet.addRow(exampleRowValues2);

  createCellDropdowns(sheet, "A", handlingUnitValues);
  createCellDropdowns(sheet, "B", containerValues);
  if (nmfc_number_enabled !== "Off") createCellDropdowns(sheet, getColumnLetterByHeader(sheet, "NMFC Class"), NMFCValues);
  if (dangerous_goods_enabled)
    createCellDropdowns(sheet, getColumnLetterByHeader(sheet, "Dangerous goods"), dangerousGoodsValues);
  if (temperature_controlled_enabled)
    createCellDropdowns(sheet, getColumnLetterByHeader(sheet, "Temperature Controlled"), temperateControlValues);

  sheet.getCell("B1").note = `Only applicable if "Handling Unit" is set to "Container"`;

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "packing_list_template.xlsx";
    link.click();
  });
}

function DownloadTemplate({ packingListConfig }: DownloadTemplateProps) {
  const intl = useIntl();

  return (
    <li className="export-tab-item " onClick={() => generatePackingListTemplate(packingListConfig, intl)}>
      <Template />
      <span className="font-12 ml-2">
        <FormattedMessage id="imports.downloadTemplate" defaultMessage="Download Template" />
      </span>
    </li>
  );
}

export default DownloadTemplate;
